import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { http } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead, Btn } from "../components/Bits";
import { SPORTS } from "../lib/site";

const FILTERS = {
  "By Product": ["Puzzle Mats", "Tatami Tiles", "Rollout Mats"],
  "By Sport": SPORTS.map((s) => s.name),
  "By Facility": ["Academy", "School or university", "Event space", "Multi-purpose hall"],
  "By Installation Type": ["Interlocking", "Individual tiles", "Rolled"],
};

const matchesFilter = (product, group, value) => {
  if (!value) return true;
  if (group === "By Product") return product.short_name === value;
  if (group === "By Sport") return (product.disciplines || []).includes(value);
  if (group === "By Installation Type")
    return (product.comparison?.installation_style || "").toLowerCase().includes(
      value.split(" ")[0].toLowerCase()
    );
  if (group === "By Facility")
    return (product.comparison?.facility_type || "").toLowerCase().includes(
      value.split(" ")[0].toLowerCase()
    );
  return true;
};

export default function Mats() {
  const { products } = useSite();
  const [group, setGroup] = useState("By Product");
  const [value, setValue] = useState("");
  const [installations, setInstallations] = useState([]);

  useSeo({
    title: "Combat Mats | Puzzle, Tatami and Rollout Mats India | MAKO Mats",
    description:
      "Professional combat mats for permanent facilities, flexible spaces and complete combat-room installations. Puzzle mats, tatami tiles and rollout mats.",
  });

  useEffect(() => {
    http.get("/content/installations").then((r) => setInstallations(r.data)).catch(() => {});
  }, []);

  const mats = products.filter((p) => p.type === "mat");
  const filtered = mats.filter((p) => matchesFilter(p, group, value));

  return (
    <div data-testid="mats-page">
      <PageHero
        eyebrow="The range"
        title="Combat mats"
        copy="Professional surface options for permanent facilities, flexible spaces and complete combat-room installations."
        image={installations[0]?.image}
        ctas={[
          { label: "Request a quote", to: "/contact", testId: "mats-quote-cta" },
          { label: "View installations", to: "/installations", variant: "outline", testId: "mats-installations-cta" },
        ]}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-wrap gap-2.5 border-b border-[#111111]/10 pb-6" data-testid="mats-filter-groups">
            {Object.keys(FILTERS).map((g) => (
              <button
                key={g}
                data-testid={`filter-group-${g.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => {
                  setGroup(g);
                  setValue("");
                }}
                className={`px-4 py-2.5 text-[0.6875rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  group === g ? "bg-[#111111] text-white" : "text-[#3B3B3B] hover:text-[#0F4C81]"
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="mats-filter-values">
            <button
              onClick={() => setValue("")}
              className={`border px-4 py-2.5 text-[0.6875rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                value === ""
                  ? "border-[#0F4C81] bg-[#0F4C81] text-white"
                  : "border-[#111111]/15 text-[#3B3B3B] hover:border-[#0F4C81] hover:text-[#0F4C81]"
              }`}
            >
              All
            </button>
            {FILTERS[group].map((v) => (
              <button
                key={v}
                data-testid={`filter-value-${v.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setValue(v)}
                className={`border px-4 py-2.5 text-[0.6875rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  value === v
                    ? "border-[#0F4C81] bg-[#0F4C81] text-white"
                    : "border-[#111111]/15 text-[#3B3B3B] hover:border-[#0F4C81] hover:text-[#0F4C81]"
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          <div className="mt-16 space-y-20 lg:space-y-28" data-testid="mats-list">
            {filtered.length === 0 && (
              <p className="text-sm text-[#7A7F85]" data-testid="mats-empty">
                No systems match that combination. Clear the filter or talk to us about your room.
              </p>
            )}
            {filtered.map((p, i) => (
              <Reveal key={p.slug}>
                <article
                  data-testid={`mat-block-${p.slug}`}
                  className="grid gap-10 border-t border-[#111111]/10 pt-10 lg:grid-cols-12 lg:gap-16"
                >
                  <div className={`lg:col-span-7 ${i % 2 ? "lg:order-2" : ""}`}>
                    <div className="relative aspect-[16/10] overflow-hidden bg-[#F5F5F2]">
                      <img
                        src={p.card_image || p.hero_image}
                        alt={p.short_name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-[900ms] hover:scale-[1.03]"
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-5">
                    <span className="mako-label text-[#0F4C81]">{p.label}</span>
                    <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-tight text-[#111111] sm:text-3xl">
                      {p.short_name}
                    </h2>
                    <p className="mt-5 text-sm leading-relaxed text-[#3B3B3B]">{p.tagline}</p>

                    <dl className="mt-9 space-y-5 border-t border-[#111111]/10 pt-7">
                      <div>
                        <dt className="mako-label text-[#7A7F85]">Main applications</dt>
                        <dd className="mt-3 text-sm text-[#3B3B3B]">
                          {(p.disciplines || []).join(" · ")}
                        </dd>
                      </div>
                      <div>
                        <dt className="mako-label text-[#7A7F85]">Installation style</dt>
                        <dd className="mt-3 text-sm text-[#3B3B3B]">
                          {p.comparison?.installation_style}
                        </dd>
                      </div>
                      <div>
                        <dt className="mako-label text-[#7A7F85]">Colour and customisation</dt>
                        <dd className="mt-3 text-sm text-[#3B3B3B]">
                          {(p.customisation || []).join(" · ")}
                        </dd>
                      </div>
                    </dl>

                    <div className="mt-10 flex flex-wrap gap-3">
                      <Btn to={`/mats/${p.slug}`} data-testid={`mat-explore-${p.slug}`}>
                        Explore {p.short_name}
                      </Btn>
                      <Link
                        to="/contact"
                        className="group inline-flex items-center gap-2 px-2 py-4 text-[0.6875rem] font-bold uppercase tracking-[0.18em] text-[#111111] transition-colors duration-300 hover:text-[#0F4C81]"
                      >
                        Enquire
                        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#111111]/10 bg-[#F5F5F2] py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead
            eyebrow="Consultative, not catalogue"
            title="We quote around your room"
            copy="MAKO is a quote-led supplier. Pricing depends on area, product, colours and customisation, so we plan the system with you before quoting."
          />
          <div className="mt-10">
            <Btn to="/contact" variant="dark" data-testid="mats-bottom-cta">
              Request a quote
            </Btn>
          </div>
        </div>
      </section>
    </div>
  );
}
